import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';

import { Button } from 'shared/components/Button';
import { FeatureFlags } from 'featureFlags/constants';
import { fireSnapchatAddCart } from 'tracking/utils/snapchatEvents';
import { fireTiktokCheckoutAddCart } from 'tracking/utils/tiktokEvents';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { InstantShipDescription } from 'productTemplate/components/buyBar/InstantShipDescription';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { logAddToCart } from 'tracking/utils/eCommerceEvents';
import { sendBuyBarTrackingEvent } from 'productTemplate/utils/sendBuyBarTrackingEvent';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { t } from 'localization/utils/t';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useLogInBeforeCheckout } from 'productTemplate/hooks/useLogInBeforeCheckout';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { VariantPriceListItemGrid } from 'productTemplate/components/buyBar/VariantPriceListItemGrid';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'productTemplate.components.buyBar.VariantPriceListItem';

export interface IVariantPriceListItemProps {
  condition: string | ReactNode;
  description: string;
  isBestPrice?: boolean;
  isInstant?: boolean;
  isUnderRetail: boolean;
  variant: IBuyBarProductVariant;
  withGridView?: VARIANT_KEYS.NEW_WITH_DEFECTS | 'special';
}

export const VariantPriceListItem = ({
  condition,
  description,
  isBestPrice,
  isUnderRetail,
  isInstant = false,
  variant,
  withGridView = null,
}: IVariantPriceListItemProps) => {
  const [isHoveringRow, setIsHoveringRow] = useState<boolean>(false);
  const [isGridOpen, setIsGridOpen] = useState<boolean>(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<boolean>(false);
  const { currentSearchTerm } = useStoreSelector((state) => state.search);
  const { productTemplate, buyBarVariants } = useStoreSelector((state) => state.pdp);
  const { id, slug, localizedSpecialDisplayPriceCents, name, brandName, sku, isFashionProduct } =
    productTemplate;

  const { mapSizeToVariants } = buyBarVariants;
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const { logInBeforeCheckout } = useLogInBeforeCheckout();
  const ConstructorClient = useConstructorClient();

  const retailPrice = localizedSpecialDisplayPriceCents?.amount;
  const showUnderRetailPrice = isUnderRetail && isBestPrice;
  const productPrice = isInstant
    ? variant?.instantShipLowestPriceCents?.amount
    : variant?.lowestPriceCents?.amount;
  const variants = mapSizeToVariants?.[variant?.sizeOption?.value];
  const isSnapchatEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_SNAPCHAT);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const isCheckoutRolloutEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_CHECKOUT_PLATFORM_ROLLOUT,
  );

  const { selectedCurrencyCode } = useCurrency();
  const currentRowRef = useRef(null);
  const router = useRouter();
  const { query } = router;

  let checkoutLink = [
    `/checkout?productTemplateId=${id}`,
    `expectedPriceCents=${productPrice}`,
    `instantShip=${String(isInstant)}`,
    `shoeCondition=${variant?.shoeCondition}`,
    `boxCondition=${variant?.boxCondition}`,
    `size=${variant?.sizeOption?.value}`,
  ].join('&');

  checkoutLink = getRedirectionPath(checkoutLink);

  const handleSelectClick = (e) => {
    const clientDedupID = uuidv4();

    sendBuyBarTrackingEvent({
      eventName: 'CART_ADD',
      productVariants: variants ? Object?.values(variants) : [],
      retailPrice: localizedSpecialDisplayPriceCents,
      sku,
      slug,
      section: '',
      size: variant.sizeOption.value,
      isUnderRetail,
      isApparelProduct: isFashionProduct,
      clientDedupID,
    });

    sendBuyBarTrackingEvent({
      eventName: 'PDP_PRICE_SIZE_SELECTION_TAP',
      productVariants: variants ? Object?.values(variants) : [],
      retailPrice: localizedSpecialDisplayPriceCents,
      sku,
      slug,
      section: '',
      size: variant.sizeOption.value,
    });

    logAddToCart({
      priceCents: productPrice,
      condition: variant.boxCondition,
      name,
      brandName,
      sku,
    });

    if (isSnapchatEnabled) {
      void fireSnapchatAddCart({
        brandName,
        productPrice,
        sku,
        clientDedupID,
      });
    }

    if (isTiktokEnabled) {
      void fireTiktokCheckoutAddCart(
        {
          contentCategory: brandName,
          contentId: sku,
          currency: selectedCurrencyCode,
          price: productPrice,
          quantity: 1,
        },
        user?.email,
      );
    }

    ConstructorClient?.tracker?.trackConversion(currentSearchTerm, {
      item_id: id.toString(),
      revenue: productPrice,
    });

    if (!isAuthenticated && !isGuestCheckoutEnabled) {
      e.preventDefault();
      logInBeforeCheckout(checkoutLink);
      return;
    }

    setIsLoadingCheckout(true);

    if (isCheckoutRolloutEnabled) {
      window.location.href = checkoutLink;
    } else {
      void router.push(checkoutLink);
    }
  };

  const toggleGridView = () => {
    setIsGridOpen(!isGridOpen);
  };

  const handleClick = (e) => {
    if (withGridView) {
      toggleGridView();
    } else if (currentRowRef.current.contains(e.target)) {
      // This is to prevent going to Checkout when closing the Instant Ship Modal
      handleSelectClick(e);
    }
  };

  const handleRowMouseLeave = () => {
    setIsHoveringRow(false);
  };

  const handleRowMouseEnter = () => {
    setIsHoveringRow(true);
  };

  const onGridLoadScrollGridToView = () => {
    // This is actually scrolling this row to the top,
    // making the next row (the grid) to scroll into view.
    // Because the sticky tab section and the table are part of the view
    // it may hide behind the sticky nav.
    currentRowRef?.current?.scrollIntoView(true);
  };

  useEffect(() => {
    if (isGridOpen) {
      setIsGridOpen(false);
    }

    if (query?.tab === SHOE_CONDITIONS.NEW && withGridView === VARIANT_KEYS.NEW_WITH_DEFECTS) {
      setIsGridOpen(true);
    }
  }, [query, variant, withGridView]);

  return (
    <>
      <VariantPriceListItem.Root
        data-qa="product_variant_link"
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
        onClick={handleClick}
        ref={currentRowRef}
      >
        <VariantPriceListItem.PriceContainer>
          <LocalizedCurrency price={productPrice} data-qa="product_variant_price" />
          {showUnderRetailPrice && (
            <VariantPriceListItem.PriceStrikethrough
              price={retailPrice}
              data-qa="product_variant_price_strike"
            />
          )}
        </VariantPriceListItem.PriceContainer>
        <VariantPriceListItem.Condition>
          <span data-qa="product_variant_condition">{condition}</span>
        </VariantPriceListItem.Condition>
        <VariantPriceListItem.Description data-qa="product_variant_description">
          {description &&
            (isInstant ? (
              <InstantShipDescription id={id} instantCopy={description} />
            ) : (
              description
            ))}
        </VariantPriceListItem.Description>
        <VariantPriceListItem.ButtonContainer>
          <VariantPriceListItem.Button
            data-qa={withGridView ? 'product_variant_view' : 'product_variant_select'}
            loading={isLoadingCheckout}
            inverted={!(isGridOpen || isHoveringRow)}
            title={withGridView ? t(`${TP}.view`, 'View') : t(`${TP}.select`, 'Select')}
          />
        </VariantPriceListItem.ButtonContainer>
      </VariantPriceListItem.Root>
      {withGridView && isGridOpen && (
        <VariantPriceListItemGrid
          variantType={withGridView}
          onGridLoad={onGridLoadScrollGridToView}
        />
      )}
    </>
  );
};

VariantPriceListItem.Root = styled.tr`
  ${variableFontRegular({ height: '12px', size: '11.5px' })};
  border-bottom: ${({ theme }) => theme.borders.white};
  vertical-align: middle;
  cursor: pointer;

  & span {
    border-bottom: 1px solid transparent;
  }
  &:hover span {
    border-bottom: ${({ theme }) => theme.borders.black};
  }
  // Increasing specificity of selector to avoid override from <Button>'s style
  &:hover button > span {
    border-bottom: none;
  }
`;

// Note: <td> doesnt obey `min-width` by nature, but `width: 0;` forces it to stay as small as possible.
VariantPriceListItem.PriceContainer = styled.td`
  width: 0;
  padding-right: 24px;
  padding-bottom: 16px;
  ${variableFontRegular({ height: '12px', size: '12px' })};
`;

VariantPriceListItem.PriceStrikethrough = styled(LocalizedCurrency)`
  margin-left: 8px;
  text-decoration: line-through;
  ${variableFontRegular({ height: '12px', size: '12px' })};
`;

VariantPriceListItem.Condition = styled.td`
  width: 0;
  padding-right: 24px;
  padding-bottom: 16px;
  white-space: nowrap;
  ${variableFontRegular({ height: '12px', size: '12px' })};
`;

VariantPriceListItem.Description = styled.td`
  ${variableFontRegular({ height: '12px', size: '10px' })};
  padding-right: 24px;
  padding-bottom: 16px;
`;

VariantPriceListItem.ButtonContainer = styled.td`
  width: 0;
  padding-bottom: 16px;
`;

VariantPriceListItem.Button = styled(Button)`
  width: auto;
  height: 20px;
  ${variableFontRegular({ size: '11.5px' })}

  margin-left: auto;
  padding: 4px;
  white-space: nowrap;
  text-transform: capitalize;

  border: ${({ theme }) => theme.borders.black};
  &:active {
    border: ${({ theme }) => theme.borders.black};
  }

  ${Button.LoaderWrapper} {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
    padding: 16px 0;
  }

  ${Button.Loader} {
    min-width: 32px;
  }
`;

VariantPriceListItem.GridView = styled.tr``;
